import { Link, NavLink, useNavigate } from 'react-router-dom';
import { ShoppingBag, LogOut, ClipboardList, Shield, BookOpen } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';

export default function Header() {
    const { user, academy, logout } = useAuth();
    const { count } = useCart();
    const nav = useNavigate();

    const navLink = (to, label) => (
        <NavLink
            to={to}
            data-testid={`nav-${to.replace(/\//g, '') || 'home'}`}
            className={({ isActive }) =>
                `font-display uppercase text-sm tracking-wider px-3 py-2 transition-colors ${
                    isActive ? 'text-crimson' : 'text-zinc-400 hover:text-white'
                }`
            }
        >
            {label}
        </NavLink>
    );

    return (
        <header className="border-hairline-b sticky top-0 z-40 bg-ink/95 backdrop-blur-md" data-testid="app-header">
            <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
                    <img
                        src="https://customer-assets.emergentagent.com/job_locals-academy-gear/artifacts/lyqjkjl7_LJJ-Logo-01-Black.png"
                        alt="Locals Jiu Jitsu"
                        className="h-10 w-10 object-contain"
                        style={{ filter: 'invert(1) brightness(1.1)' }}
                    />
                    <div className="leading-none">
                        <div className="font-display text-lg uppercase tracking-tighter">Locals HQ</div>
                        <div className="font-mono-data text-[10px] text-zinc-500 uppercase">Apparel Portal</div>
                    </div>
                </Link>

                <nav className="flex items-center overflow-x-auto">
                    {navLink('/', 'Catalog')}
                    {navLink('/price-list', 'Price List')}
                    {navLink('/supplier', 'Supplier')}
                    {user?.role === 'academy' && navLink('/orders', 'My Orders')}
                    {user?.role === 'admin' && navLink('/admin', 'HQ Admin')}
                </nav>

                <div className="flex items-center gap-3">
                    {user && (
                        <Link to="/cart" data-testid="cart-link"
                              className="relative font-display uppercase text-sm tracking-wider text-white hover:text-crimson transition-colors flex items-center gap-2">
                            <ShoppingBag size={18} />
                            <span className="hidden sm:inline">Cart</span>
                            {count > 0 && (
                                <span data-testid="cart-count" className="absolute -top-2 -right-3 bg-crimson text-white text-[10px] font-mono-data px-1.5 py-0.5 leading-none">
                                    {count}
                                </span>
                            )}
                        </Link>
                    )}
                    {user ? (
                        <div className="flex items-center gap-3 pl-4 border-l border-zinc-800">
                            <div className="text-right hidden sm:block">
                                <div className="font-mono-data text-[10px] uppercase text-zinc-500">
                                    {user.role === 'admin' ? 'HQ Admin' : academy?.name}
                                </div>
                                <div className="font-display text-sm uppercase">{user.username}</div>
                            </div>
                            <button
                                data-testid="logout-btn"
                                onClick={() => { logout(); nav('/login'); }}
                                className="p-2 text-zinc-400 hover:text-crimson transition-colors"
                                title="Logout"
                            >
                                <LogOut size={18} />
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" data-testid="login-link"
                              className="font-display uppercase text-sm tracking-wider px-4 py-2 bg-brand text-black hover:bg-brand-hover transition-colors">
                            Login
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}
